import type { SupabaseClient } from '@supabase/supabase-js';
import { renderOffersTab } from './tabs/offers.js';
import { renderOrdersTab } from './tabs/orders.js';
import { renderOttoListTab } from './tabs/otto-list.js';
import { renderLogTab } from './tabs/log.js';
import { renderOttoSettings } from './otto-settings.js';

interface DashboardContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  handshake: { appToken: string; appIdentifier?: string; offlineToken?: string };
  supabaseUrl: string;
}

interface Tab {
  id: string;
  label: string;
  icon: string; // Font-Awesome-Klasse, z.B. 'fa-receipt'
  render: (host: HTMLElement, ctx: DashboardContext) => void | Promise<void>;
}

/* Tab-IDs (orders / listings / lister / settings) folgen der Design-Reference.
 * Die Renderer-Funktionen behalten ihre bisherigen internen Namen
 * (renderOffersTab, renderOttoListTab) — werden nur unter den neuen IDs
 * registriert. Markup-Refactor & Funktionserhalt passiert in 3c-3f. */
const TABS: Tab[] = [
  { id: 'orders',   label: 'Bestellungen',     icon: 'fa-receipt',                  render: renderOrdersTab },
  { id: 'listings', label: 'Angebote',         icon: 'fa-tag',                      render: renderOffersTab },
  { id: 'lister',   label: 'Lister',           icon: 'fa-arrows-left-right-to-line',render: renderOttoListTab },
  { id: 'log',      label: 'Log',              icon: 'fa-list',                     render: (host, ctx) => renderLogTab(host, ctx, { embedded: true }) },
  { id: 'settings', label: 'Einstellungen',    icon: 'fa-sliders',                  render: (host, ctx) => renderOttoSettings({ ...ctx, contentEl: host }) },
];

function deriveInitials(source: string): string {
  // Initialen-Heuristik fuer den Avatar:
  //   - "GO eCommerce" → "GE"           (erste Buchstaben der ersten 2 Worte)
  //   - "Werkzeugkasten24 GmbH" → "WG"  (zwei Worte, erste Buchstaben)
  //   - "Werkzeugkasten24" → "W24"      (Wort+Zahlsuffix → Buchstabe+Zahl)
  //   - "Acme" → "AC"                   (erste 2 Buchstaben)
  //   - "" → ''                         (Avatar zeigt nichts an)
  const trimmed = source.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  // Single-Word: Buchstabe+Zahl (z.B. "Werkzeugkasten24" → "W24")
  const m = trimmed.match(/^([A-Za-zÄ-ſ])(?:[a-zÄ-ſ]*?)(\d{1,3})\b/);
  if (m) return (m[1]! + m[2]!).toUpperCase();
  // Sonst: erste 2 Buchstaben (kein Zahl-Suffix)
  const onlyLetters = trimmed.replace(/[^A-Za-zÄ-ſ]/g, '');
  return (onlyLetters.slice(0, 2) || trimmed.slice(0, 2)).toUpperCase();
}

export async function renderDashboard(
  ctx: DashboardContext,
) {
  ctx.contentEl.hidden = false;

  // Tenant-Name asynchron nachladen — initial zeigen wir einen Platzhalter,
  // sobald der Name kommt, wird kl-brand-sub + Avatar-Initials updated. Avatar
  // bekommt keinen UUID-Hex-Suffix mehr — bis der name da ist, nur ".." als
  // dezenter Loading-State.
  const initialsFallback = '··';

  const headerHtml = `
      <header class="kl-header">
        <div class="kl-brand">
          <img class="kl-brand-logo" src="/logo-black.png" alt="go!e Commerce" width="835" height="338" />
          <div class="kl-brand-text">
            <div class="kl-brand-line"><span class="kl-brand-slash">Otto</span></div>
            <div class="kl-brand-sub" id="kl-brand-account">Lade Account …</div>
          </div>
        </div>
        <div class="kl-header-right">
          <span class="kl-connected"><span class="v-icon fa-light fa-circle-check"></span> verbunden</span>
          <div class="kl-user">
            <div class="kl-user-avatar" id="kl-user-avatar">${initialsFallback}</div>
          </div>
        </div>
      </header>`;

  ctx.contentEl.innerHTML = `
    <div class="kl-app">
      ${headerHtml}
      <nav class="kl-tabs" id="kl-tabs" role="tablist"></nav>
      <main class="kl-tab-panel" id="kl-content"></main>
    </div>
  `;

  // account.tenants.name laden und in die Brand-Sub eintragen. RLS sorgt
  // dafuer, dass der eingeloggte User nur seinen eigenen Tenant sieht.
  void (async () => {
    const { data, error } = await ctx.supabase
      .schema('account')
      .from('tenants')
      .select('name')
      .eq('id', ctx.session.tenantId)
      .maybeSingle();
    const subEl = document.getElementById('kl-brand-account');
    const avatarEl = document.getElementById('kl-user-avatar');
    if (error || !data?.name) {
      // Kein Name verfuegbar: Brand-Sub komplett verstecken (keine UUID-
      // Anzeige), Avatar bleibt mit '..' Loading-State.
      if (subEl) {
        subEl.textContent = '';
        subEl.style.display = 'none';
      }
      return;
    }
    if (subEl) subEl.textContent = data.name;
    if (avatarEl) avatarEl.textContent = deriveInitials(data.name);
  })();

  // Status-Dot aus dem Page-Chrome ausblenden — die kl-connected-Anzeige im
  // Header ersetzt ihn. Bleibt im DOM für den Init-/Error-Pfad in main.ts.
  const dot = document.querySelector<HTMLSpanElement>('#status-dot');
  if (dot) dot.style.display = 'none';

  const tabsEl = ctx.contentEl.querySelector<HTMLElement>('#kl-tabs')!;
  const body = ctx.contentEl.querySelector<HTMLElement>('#kl-content')!;

  // Wenn der User aus dem Vario-Slot ueber "Im Lister bearbeiten" hierhergeschaltet
  // hat, liegt `otto.focus` im localStorage (siehe listing-status.ts) — mit einem
  // `ts`-Timestamp. Wir akzeptieren nur Eintraege juenger als 30s; alles aeltere ist
  // ein Leichenrest (Auto-Focus-Konsum hat nicht gegriffen, Browser-Reload, alter
  // Tab) und darf nicht dazu fuehren, dass die App immer im Lister startet. Alte
  // Eintraege werden hier hart geloescht, damit sie nicht in der naechsten Session
  // erneut greifen.
  const hasFreshFocus = (() => {
    try {
      const raw = localStorage.getItem('otto.focus');
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { ts?: number };
      if (typeof parsed?.ts !== 'number' || Date.now() - parsed.ts > 30_000) {
        localStorage.removeItem('otto.focus');
        return false;
      }
      return true;
    } catch {
      // JSON kaputt → wegraeumen.
      try { localStorage.removeItem('otto.focus'); } catch { /* ignore */ }
      return false;
    }
  })();
  let active = (hasFreshFocus && TABS.some((t) => t.id === 'lister')) ? 'lister' : TABS[0]!.id;

  function renderTabBar() {
    tabsEl.innerHTML = TABS.map(
      (t) => `
        <button type="button"
                class="kl-tab ${active === t.id ? 'is-active' : ''}"
                data-tab="${t.id}"
                role="tab"
                aria-selected="${active === t.id}">
          <span class="v-icon fa-light ${t.icon}"></span>
          ${t.label}
        </button>
      `,
    ).join('');
    for (const btn of tabsEl.querySelectorAll<HTMLButtonElement>('.kl-tab')) {
      btn.addEventListener('click', () => activate(btn.dataset.tab!));
    }
  }

  async function activate(tabId: string) {
    const tab = TABS.find((t) => t.id === tabId) ?? TABS[0]!;
    active = tab.id;
    renderTabBar();
    body.innerHTML = '';
    await tab.render(body, ctx);
  }

  renderTabBar();
  await activate(active);
}
